import React, { useState, useRef, useMemo, useEffect } from "react";
import { Modal, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View, ActivityIndicator } from "react-native";
import { useTheme } from "../../../theme";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../store";
import { TicketingDuplicateCandidate, searchTickets } from "../../../store/thunks/submitTicketThunk";
import styles from "../TicketDetailScreenStyles";
import {
  normalizeMergeStatus,
  normalizeUrgencyKey,
  toTitleCaseWord,
} from "../../../utils/ticketNormalization";
import {
  MERGE_STATUS_LABEL,
  MERGE_STATUS_BG,
  MERGE_STATUS_COLOR,
  MERGEABLE_STATUSES,
  isMergeable,
  mergeDisabledTooltip,
} from "../../../constants/ticketStatusConstants";

// ── ADO dropdown options ──────────────────────────────────────────────────────
const ADO_PROJECT_OPTIONS = ["Navigator", "WES", "Reporting Platform"];
const WORK_ITEM_TYPE_OPTIONS = ["Bug", "User Story", "Feature"];
const AREA_PATH_OPTIONS = [
  "Navigator\\Dock Manager",
  "Navigator\\Labor",
  "Navigator\\Observations",
  "Navigator\\Platform",
];
const ITERATION_OPTIONS = ["Navigator\\PI-3\\Iteration 2", "Navigator\\PI-3\\Backlog"];
const PRIORITY_OPTIONS = ["1 - Critical", "2 - High", "3 - Medium", "4 - Low"];
const SEVERITY_OPTIONS = ["1 - Critical", "2 - High", "3 - Medium", "4 - Low"];
const WES_SEVERITY_OPTIONS = ["1 - Critical", "2 - High", "3 - Medium", "5 - Informational"];

// ── Template mappings for category/subcategory combinations ────────────────────
const REPORTING_NEW_TEMPLATE = {
  workItemType: "User Story",
  adoBoard: "Analytics",
  assignedTo: "Emily Ruhala",
  tags: "New Report",
};

const REPORTING_ENHANCEMENT_TEMPLATE = {
  workItemType: "User Story",
  adoBoard: "Analytics",
  assignedTo: "Emily Ruhala",
  tags: "Enhancement",
};

const REPORTING_FIX_TEMPLATE = {
  workItemType: "Bug",
  adoBoard: "Analytics",
  assignedTo: "Emily Ruhala",
  tags: "Fix",
};

const SYSTEM_ISSUE_VISION_PICK_TEMPLATE = {
  workItemType: "Bug",
  adoProject: "WES",
  adoBoard: "WES Bug Review",
};

const SYSTEM_ISSUE_BUG_REVIEW_TEMPLATE = {
  workItemType: "Bug",
  adoProject: "WES",
  adoBoard: "WES Bug Review",
  priority: "3",
};

const SYSTEM_ISSUE_NAVIGATOR_TEMPLATE = {
  workItemType: "Bug",
  adoProject: "Navigator",
  adoBoard: "Navigator Board",
};

const SYSTEM_ENHANCEMENT_NAVIGATOR_TEMPLATE = {
  workItemType: "User Story",
  adoProject: "Navigator",
  adoBoard: "Navigator Board",
};

// ── Helper function to map ticket urgency to ADO priority format ───────────────
// Converts urgency like "critical", "high", "medium", "low" to ADO format "1 - Critical" etc
function mapUrgencyToAdoPriority(urgency?: string): string {
  if (!urgency) return PRIORITY_OPTIONS[0];
  
  const urgencyLower = String(urgency).toLowerCase().trim();
  switch (urgencyLower) {
    case "critical":
      return "1 - Critical";
    case "high":
      return "2 - High";
    case "medium":
      return "3 - Medium";
    case "low":
      return "4 - Low";
    default:
      return PRIORITY_OPTIONS[0];
  }
}

// ── Shared inline dropdown component ─────────────────────────────────────────
type AdoDropdownProps = {
  value: string;
  options: string[];
  onChange: (v: string) => void;
  openKey: string;
  currentOpenKey: string | null;
  setOpenKey: (k: string | null) => void;
};

const AdoDropdown: React.FC<AdoDropdownProps> = ({
  value,
  options,
  onChange,
  openKey,
  currentOpenKey,
  setOpenKey,
}) => {
  const isOpen = currentOpenKey === openKey;

  if (Platform.OS === "web") {
    return (
      // @ts-ignore — native <select> on web
      <select
        value={value}
        onChange={(e: any) => onChange(e.target.value)}
        style={{
          width: "100%",
          height: 36,
          fontSize: 13,
          fontWeight: "500",
          paddingLeft: 10,
          paddingRight: 28,
          borderRadius: 7,
          border: "1px solid #D0D0D0",
          backgroundColor: "#ffffff",
          color: "#2D2D2D",
          cursor: "pointer",
          appearance: "auto",
        } as any}
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }

  return (
    <View style={{ position: "relative", zIndex: isOpen ? 100 : 1 }}>
      <Pressable
        onPress={() => setOpenKey(isOpen ? null : openKey)}
        style={({ pressed }) => [styles.triageSelectTrigger, pressed && styles.btnPressed]}
      >
        <Text style={styles.triageSelectText}>{value}</Text>
        <MaterialIcons
          name={isOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
          size={16}
          color="#374151"
        />
      </Pressable>
      {isOpen && (
        <View style={[styles.triageSelectMenu, { maxHeight: 180 }]}>
          <ScrollView nestedScrollEnabled>
            {options.map((opt) => (
              <Pressable
                key={opt}
                onPress={() => { onChange(opt); setOpenKey(null); }}
                style={({ pressed }) => [
                  styles.triageSelectMenuItem,
                  opt === value && styles.triageSelectMenuItemActive,
                  pressed && styles.btnPressed,
                ]}
              >
                <Text style={[
                  styles.triageSelectMenuItemText,
                  opt === value && styles.triageSelectMenuItemTextActive,
                ]}>
                  {opt}
                </Text>
              </Pressable>
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
};



type Props = {
  mergeOpen: boolean;
  adoOpen: boolean;
  primaryTicketId?: string;
  primaryTicketTitle?: string;
  adoCategory?: string;
  adoSubcategory?: string;
  adoDescription?: string;
  submitterEmail?: string;
  assignedToName?: string;
  ticketPriority?: string;
  dashboardLink?: string;
  ticketVersion?: string;
  ticketSite?: string;
  ticketWesEquipment?: string;
  ticketDateTimeStamp?: string;
  ticketPreconditions?: string;
  ticketStepsToReproduce?: string;
  ticketExpectedResults?: string;
  ticketCurrentResults?: string;
  ticketModule?: string;
  ticketIssueDescription?: string;
  onCloseMerge: () => void;
  onConfirmMerge: (selectedIds: string[]) => Promise<void>;
  /** Called after a successful merge when the user chooses to flag the ticket as Widespread */
  onFlagWidespread?: () => void;
  onCloseAdo: () => void;
  onConfirmAdo: (payload: {
    project: string;
    workItemType: string;
    title: string;
    reproStepsHtml: string;
    severity: string;
    priority: number;
    [key: string]: any;
  }) => Promise<{ msg: string; type: "success" | "info" | "warning" | "error" }>;
};

type MergeSearchResult = {
  ticketNumber: string;
  title: string;
  status: string;
  category?: string;
  subCategory?: string;
  submitterEmail?: string;
  levelReferenceId?: string;
  /** Accurate location count from the search API response */
  totalImpactedLocations?: number;
};

type SelectedMergeTicketView = {
  id: string;
  title: string;
  status?: string;
  submittedBy?: string;
  location?: string;
  impactSites?: number;
  urgency?: string;
};

const TicketDetailActionModals: React.FC<Props> = ({
  mergeOpen,
  adoOpen,
  primaryTicketId,
  primaryTicketTitle,
  adoCategory,
  adoSubcategory,
  adoDescription,
  submitterEmail,
  assignedToName,
  ticketPriority,
  dashboardLink,
  ticketVersion,
  ticketSite,
  ticketWesEquipment,
  ticketDateTimeStamp,
  ticketPreconditions,
  ticketStepsToReproduce,
  ticketExpectedResults,
  ticketCurrentResults,
  ticketModule,
  ticketIssueDescription,
  onCloseMerge,
  onConfirmMerge,
  onFlagWidespread,
  onCloseAdo,
  onConfirmAdo,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [mergeSearchQuery, setMergeSearchQuery] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showWidespreadPrompt, setShowWidespreadPrompt] = useState(false);
  const [mergeValidationError, setMergeValidationError] = useState<string | null>(null);
  const [isMergeSubmitting, setIsMergeSubmitting] = useState(false);

  const [mergeSearchResults, setMergeSearchResults] = useState<MergeSearchResult[]>([]);
  const [selectedTicketSnapshot, setSelectedTicketSnapshot] = useState<Record<string, SelectedMergeTicketView>>({});
  const [isSearching, setIsSearching] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const ticketDetails = useSelector((state: RootState) => state.ticketingPortal.ticketDetails);
  // Needed for merge impact summary and status validation
  const sustainQueue = useSelector((state: RootState) => state.ticketingPortal.sustainQueue);
  // Get all locations for Site dropdown and Location ID auto-population
  const appLocations = useSelector((state: RootState) => (state as any).appData?.locations) as Array<{
    id: string;
    name: string;
    locSapId?: string;
  }> | undefined;
  const selectedLocation = useSelector((state: RootState) => (state as any).appData?.selectedLocation);

  useEffect(() => {
    if (mergeOpen) {
      setSelectedIds([]);
      setMergeSearchQuery("");
      setShowConfirmDialog(false);
      setMergeValidationError(null);
      setMergeSearchResults([]);
      setSelectedTicketSnapshot({});
    }
  }, [mergeOpen]);

  useEffect(() => {
    let ignore = false;
    const q = mergeSearchQuery.trim();
    if (!q) {
      setMergeSearchResults([]);
      return;
    }
    setIsSearching(true);
    // Call API to search for tickets
    dispatch(searchTickets(q))
      .then((candidates: TicketingDuplicateCandidate[]) => {
        if (!ignore) {
          const mapped: MergeSearchResult[] = (candidates || [])
            .map((t) => ({
              ticketNumber: t.ticketNumber,
              title: t.title,
              status: String(t.status ?? ""),
              category: t.category,
              subCategory: t.subcategory,
              submitterEmail: t.submitterEmail,
              levelReferenceId: t.levelReferenceId,
              totalImpactedLocations: t.totalImpactedLocations ?? undefined,
            }));
          // Exclude primary, already-selected, and non-mergeable statuses
          setMergeSearchResults(
            mapped
              .filter((t) => {
                // Exclude primary ticket
                if (t.ticketNumber === primaryTicketId) return false;
                // Exclude already-selected tickets
                if (selectedIds.includes(t.ticketNumber)) return false;
                
                // Exclude closed/resolved/merged tickets
                const rawStatus = String(t.status || "").toLowerCase();
                const normalizedStatus = normalizeMergeStatus(rawStatus);
                if (!isMergeable(normalizedStatus)) {
                  console.log(`[Merge Filter] Excluding ${t.ticketNumber}: status "${rawStatus}" -> "${normalizedStatus}" is not mergeable`);
                  return false;
                }
                
                // Exclude tickets already merged into ANY ticket (check both sustainQueue and ticketDetails)
                const queueRow = sustainQueue?.find((q) => q.id === t.ticketNumber);
                if (queueRow?.mergedIntoTicketId) {
                  console.log(`[Merge Filter] Excluding ${t.ticketNumber}: already merged into ${queueRow.mergedIntoTicketId}`);
                  return false;
                }
                
                const detailRow = ticketDetails[t.ticketNumber];
                if (detailRow?.mergedIntoTicketId) {
                  console.log(`[Merge Filter] Excluding ${t.ticketNumber}: already merged into ${detailRow.mergedIntoTicketId}`);
                  return false;
                }
                
                return true;
              })
              .slice(0, 10)
          );
          setIsSearching(false);
        }
      })
      .catch(() => {
        if (!ignore) {
          setMergeSearchResults([]);
          setIsSearching(false);
        }
      });
    return () => { ignore = true; };
  }, [mergeSearchQuery, primaryTicketId, selectedIds, sustainQueue, ticketDetails, dispatch]);

  // Look up full ticket data for selected IDs (from search results or ticketDetails)
  const selectedTickets = useMemo<SelectedMergeTicketView[]>(
    () => selectedIds.map((id) => {
      const snapshot = selectedTicketSnapshot[id];
      const fromSearch = mergeSearchResults.find((t) => t.ticketNumber === id);
      const fromQueue = sustainQueue.find((t) => t.id === id);
      const fromDetail = ticketDetails[id];
      return {
        id,
        title: snapshot?.title ?? fromSearch?.title ?? fromQueue?.title ?? fromDetail?.title ?? id,
        status: snapshot?.status ?? fromSearch?.status ?? fromQueue?.status ?? fromDetail?.status,
        submittedBy: snapshot?.submittedBy ?? fromQueue?.submittedBy ?? fromDetail?.submittedBy ?? fromSearch?.submitterEmail,
        location: snapshot?.location ?? fromQueue?.location ?? fromSearch?.levelReferenceId,
        impactSites: snapshot?.impactSites ?? fromQueue?.impactSites ?? fromDetail?.affectedLocations?.length,
        urgency: snapshot?.urgency ?? fromQueue?.urgency ?? fromDetail?.urgency,
      };
    }),
    [selectedIds, selectedTicketSnapshot, mergeSearchResults, sustainQueue, ticketDetails]
  );

  const selectedCount = selectedIds.length;

  function addToMerge(ticket: MergeSearchResult) {
    setMergeValidationError(null);
    const id = ticket.ticketNumber;
    const queueRow = sustainQueue.find((t) => t.id === id);
    const detailRow = ticketDetails[id];
    setSelectedTicketSnapshot((prev) => ({
      ...prev,
      [id]: {
        id,
        title: ticket.title || queueRow?.title || detailRow?.title || id,
        status: normalizeMergeStatus(ticket.status || queueRow?.status || detailRow?.status),
        submittedBy: ticket.submitterEmail || queueRow?.submittedBy || detailRow?.submittedBy,
        location: ticket.levelReferenceId || queueRow?.location,
        // Prefer the accurate totalImpactedLocations from the search API response
        impactSites: ticket.totalImpactedLocations ?? queueRow?.impactSites ?? detailRow?.affectedLocations?.length,
        urgency: queueRow?.urgency ?? detailRow?.urgency,
      },
    }));
    setSelectedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }

  function removeFromMerge(id: string) {
    setMergeValidationError(null);
    setSelectedIds((prev) => prev.filter((x) => x !== id));
    setSelectedTicketSnapshot((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  // ── Merge Impact Summary ──────────────────────────────────────────────────
  const URGENCY_ORDER = ["critical", "high", "medium", "low"] as const;
  type UrgencyKey = (typeof URGENCY_ORDER)[number];

  const primaryQueueEntry = useMemo(
    () => sustainQueue.find((t) => t.id === primaryTicketId),
    [sustainQueue, primaryTicketId]
  );
  const primaryDetailEntry = primaryTicketId ? ticketDetails[primaryTicketId] : null;

  const impactSummary = useMemo(() => {
    if (selectedCount === 0) return null;

    // Submitters
    const primarySubmitter =
      primaryDetailEntry?.submittedBy || primaryQueueEntry?.submittedBy || "Primary submitter";
    const additionalSet = new Set(
      selectedTickets.map((t) => t.submittedBy).filter((s): s is string => !!s && s !== primarySubmitter)
    );

    // Locations — prefer totalImpactedLocations from the ticket record (set by backend),
    // then fall back to affectedLocations array length, then queue impactSites, then 1.
    const primaryLocations =
      (primaryDetailEntry as any)?.totalImpactedLocations ??
      primaryDetailEntry?.affectedLocations?.length ??
      primaryQueueEntry?.impactSites ??
      1;
    const absorbedLocations = selectedTickets.reduce((sum, t) => sum + (t.impactSites ?? 1), 0);
    const totalLocations = primaryLocations + absorbedLocations;

    // Urgency — pick highest across primary + selected
    const primaryUrgency = normalizeUrgencyKey(primaryDetailEntry?.urgency || primaryQueueEntry?.urgency) as UrgencyKey;
    const allUrgencies: UrgencyKey[] = [
      primaryUrgency,
      ...selectedTickets.map((t) => normalizeUrgencyKey(t.urgency) as UrgencyKey),
    ];
    const highestUrgency = allUrgencies.reduce<UrgencyKey>((best, cur) => {
      return URGENCY_ORDER.indexOf(cur) < URGENCY_ORDER.indexOf(best) ? cur : best;
    }, "low");
    const urgencyRaised = URGENCY_ORDER.indexOf(highestUrgency) < URGENCY_ORDER.indexOf(primaryUrgency);

    // ServiceNow
    const primaryIncident = primaryDetailEntry?.incidentId || primaryQueueEntry?.incidentId;

    // Attachments — sum from primary detail + absorbed tickets (queue rows don't carry attach counts, use 0)
    const primaryAttachments = 0; // attachment counts not stored in queue rows
    const totalAttachments = primaryAttachments;

    return {
      primarySubmitter,
      additionalSubmittersCount: additionalSet.size,
      totalLocations,
      highestUrgency,
      urgencyRaised,
      primaryUrgency,
      primaryIncident,
      totalAttachments,
    };
  }, [selectedCount, selectedTickets, primaryDetailEntry, primaryQueueEntry]);
  
  // Detect if this is a Reporting template (New, Enhancement, or Fix)
  const isReportingNewTemplate = adoCategory === "Reporting" && adoSubcategory === "New";
  const isReportingEnhancementTemplate = adoCategory === "Reporting" && adoSubcategory === "Enhancement";
  const isReportingFixTemplate = adoCategory === "Reporting" && adoSubcategory === "Fix";
  const isReportingTemplate = isReportingNewTemplate || isReportingEnhancementTemplate || isReportingFixTemplate;
  
  // Detect if this is a Vision Pick (WES) - System Issue template
  const isSystemIssueVisionPickTemplate = adoCategory === "Vision Pick (WES)" && adoSubcategory === "System Issue";
  
  // Detect if this is a System Issue - Bug Review (WES) template
  const isSystemIssueBugReviewTemplate = adoCategory === "System Issue" && adoSubcategory === "Bug Review (WES)";
  
  // Detect if this is a Navigator - System Issue template
  const isSystemIssueNavigatorTemplate = adoCategory === "Navigator" && adoSubcategory === "System Issue";
  
  // Detect if this is a Navigator - System Enhancement template
  const isSystemEnhancementNavigatorTemplate = adoCategory === "Navigator" && adoSubcategory === "System Enhancement";
  
  // Get the appropriate template based on subcategory
  const getReportingTemplate = () => {
    if (isReportingNewTemplate) return REPORTING_NEW_TEMPLATE;
    if (isReportingEnhancementTemplate) return REPORTING_ENHANCEMENT_TEMPLATE;
    if (isReportingFixTemplate) return REPORTING_FIX_TEMPLATE;
    return null;
  };
  const reportingTemplate = getReportingTemplate();
  
  const [adoProject, setAdoProject] = useState(ADO_PROJECT_OPTIONS[0]);
  const [workItemType, setWorkItemType] = useState(WORK_ITEM_TYPE_OPTIONS[0]);
  const [priority, setPriority] = useState(PRIORITY_OPTIONS[0]);
  const [severity, setSeverity] = useState(SEVERITY_OPTIONS[0]);
  // Determine which severity options to use based on template type
  const severityOptions = (isSystemIssueVisionPickTemplate || isSystemIssueBugReviewTemplate) ? WES_SEVERITY_OPTIONS : SEVERITY_OPTIONS;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [acceptanceCriteria, setAcceptanceCriteria] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [adoBoard, setAdoBoard] = useState("Analytics");
  const [assignedToAdo, setAssignedToAdo] = useState("Emily Ruhala");
  const [tags, setTags] = useState("New Report");
  // System Issue - Vision Pick (WES) specific fields
  const [expectedResult, setExpectedResult] = useState("");
  const [currentResult, setCurrentResult] = useState("");
  const [wesVersion, setWesVersion] = useState("");
  const [wesEquipment, setWesEquipment] = useState("");
  const [errorTimeWithTimezone, setErrorTimeWithTimezone] = useState("");
  const [preconditions, setPreconditions] = useState("");
  const [stepsToReproduce, setStepsToReproduce] = useState("");
  const [environment, setEnvironment] = useState("Prod");
  const [bugSeverity, setBugSeverity] = useState("1");
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [siteInput, setSiteInput] = useState("");
  const [locationId, setLocationId] = useState("");
  const [isAdoSubmitting, setIsAdoSubmitting] = useState(false);
  const [adoSubmitError, setAdoSubmitError] = useState<string | null>(null);
  const [adoRetryCount, setAdoRetryCount] = useState(0);
  const [touched, setTouched] = useState<{ [k: string]: boolean }>({});
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const initialState = useRef({
    adoProject: ADO_PROJECT_OPTIONS[0],
    workItemType: WORK_ITEM_TYPE_OPTIONS[0],
    priority: PRIORITY_OPTIONS[0],
    severity: SEVERITY_OPTIONS[0],
    title: "",
    description: "",
    acceptanceCriteria: "",
    targetDate: "",
    adoBoard: "Analytics",
    assignedToAdo: "Emily Ruhala",
    tags: "New Report",
  });

  // Computed board name for the modal subtitle
  const adoBoardDisplay = isReportingTemplate ? "Analytics"
    : (isSystemIssueVisionPickTemplate || isSystemIssueBugReviewTemplate) ? "WES Bug Review"
    : (isSystemIssueNavigatorTemplate || isSystemEnhancementNavigatorTemplate) ? "Navigator Board"
    : adoProject;

  useEffect(() => {
    if (!adoOpen) {
      setIsAdoSubmitting(false);
      setAdoSubmitError(null);
      setAdoRetryCount(0);
      return;
    }

    const normalizedTitle = primaryTicketTitle?.trim() || "";
    const ticketTitle = normalizedTitle || "Ticket issue";
    
    let computedTitle: string;
    let computedDescription: string;
    let computedAcceptanceCriteria: string = "";
    let computedWorkItemType: string;
    let computedAdoProject: string;
    
    // Template-specific logic for System Issue - Vision Pick (WES)
    if (isSystemIssueVisionPickTemplate) {
      // Title: Direct copy from ticket title
      computedTitle = ticketTitle;
      
      // Description format with 10 sections for Vision Pick
      computedDescription = [
        "Requestor",
        `- Email: ${submitterEmail || "N/A"}`,
        "",
        "Created By",
        `- Team Member: ${assignedToName || "N/A"}`,
        "",
        "Version",
        `- ${ticketVersion || "N/A"}`,
        "",
        "Site",
        `- ${ticketSite || "N/A"}`,
        "",
        "WES Equipment",
        `- ${ticketWesEquipment || "N/A"}`,
        "",
        "Date/Time Stamp",
        `- ${ticketDateTimeStamp || "N/A"}`,
        "",
        "Preconditions",
        `- ${ticketPreconditions || "N/A"}`,
        "",
        "Steps to Reproduce",
        `- ${ticketStepsToReproduce || "N/A"}`,
        "",
        "Expected Results",
        `- ${ticketExpectedResults || "N/A"}`,
        "",
        "Current Results",
        `- ${ticketCurrentResults || "N/A"}`,
      ].join("\n");
      
      // Template constants
      computedWorkItemType = SYSTEM_ISSUE_VISION_PICK_TEMPLATE.workItemType;
      computedAdoProject = SYSTEM_ISSUE_VISION_PICK_TEMPLATE.adoProject;
      
      setWorkItemType(computedWorkItemType);
      setAdoProject(computedAdoProject);
      setAdoBoard(SYSTEM_ISSUE_VISION_PICK_TEMPLATE.adoBoard);
      setAcceptanceCriteria("");
      setTargetDate("");
      setAssignedToAdo("");
      setTags("");
      setEnvironment("Prod");
      setBugSeverity("1");
      // Initialize individual Vision Pick description fields from ticket props
      setWesVersion("");
      // Find location matching ticket's levelReferenceId in appLocations
      const currentTicket = primaryTicketId ? ticketDetails[primaryTicketId] : null;
      const ticketLocationRef = (currentTicket as any)?.levelReferenceId;
      const matchedLocation = ticketLocationRef && appLocations
        ? appLocations.find((loc) => loc.name === ticketLocationRef)
        : null;
      setSiteInput(matchedLocation?.name || "");
      setLocationId(matchedLocation?.id || "");
      setWesEquipment(ticketWesEquipment || "");
      setErrorTimeWithTimezone(ticketDateTimeStamp || "");
      setPreconditions(ticketPreconditions || "");
      setStepsToReproduce(ticketStepsToReproduce || "");
      setExpectedResult(ticketExpectedResults || "");
      setCurrentResult(ticketCurrentResults || "");
      
    // Template-specific logic for System Issue - Bug Review (WES)
    } else if (isSystemIssueBugReviewTemplate) {
      // Title: Direct copy from ticket title
      computedTitle = ticketTitle;
      
      // Description format for Bug Review template with multiple field sections
      computedDescription = [
        "Requestor",
        `- Email: ${submitterEmail || "N/A"}`,
        "",
        "Created By",
        `- Team Member: ${assignedToName || "N/A"}`,
        "",
        "Version",
        `- ${ticketVersion || "N/A"}`,
        "",
        "Site",
        `- ${ticketSite || "N/A"}`,
        "",
        "WES Equipment",
        `- ${ticketWesEquipment || "N/A"}`,
        "",
        "Date/Time Stamp",
        `- ${ticketDateTimeStamp || "N/A"}`,
        "",
        "Preconditions",
        `- ${ticketPreconditions || "N/A"}`,
        "",
        "Steps to Reproduce",
        `- ${ticketStepsToReproduce || "N/A"}`,
        "",
        "Expected Results",
        `- ${ticketExpectedResults || "N/A"}`,
        "",
        "Current Results",
        `- ${ticketCurrentResults || "N/A"}`,
      ].join("\n");
      
      // Template constants
      computedWorkItemType = SYSTEM_ISSUE_BUG_REVIEW_TEMPLATE.workItemType;
      computedAdoProject = SYSTEM_ISSUE_BUG_REVIEW_TEMPLATE.adoProject;
      
      setWorkItemType(computedWorkItemType);
      setAdoProject(computedAdoProject);
      setAdoBoard(SYSTEM_ISSUE_BUG_REVIEW_TEMPLATE.adoBoard);
      setPriority("3 - Medium");
      setAcceptanceCriteria("");
      setTargetDate("");
      setAdoBoard("WES Bug Review");
      setAssignedToAdo("");
      setTags("");
      setEnvironment("Prod");
      setBugSeverity("1");
      // Initialize individual Bug Review description fields from ticket props
      setWesVersion(ticketVersion || "");
      // Find location matching ticket's levelReferenceId in appLocations
      const currentTicket2 = primaryTicketId ? ticketDetails[primaryTicketId] : null;
      const ticketLocationRef2 = (currentTicket2 as any)?.levelReferenceId;
      const matchedLocation2 = ticketLocationRef2 && appLocations
        ? appLocations.find((loc) => loc.name === ticketLocationRef2)
        : null;
      setSiteInput(matchedLocation2?.name || "");
      setLocationId(matchedLocation2?.id || "");
      setWesEquipment(ticketWesEquipment || "");
      setErrorTimeWithTimezone(ticketDateTimeStamp || "");
      setPreconditions(ticketPreconditions || "");
      setStepsToReproduce(ticketStepsToReproduce || "");
      setExpectedResult(ticketExpectedResults || "");
      setCurrentResult(ticketCurrentResults || "");
      
    // Template-specific logic for System Issue - Navigator
    } else if (isSystemIssueNavigatorTemplate) {
      // Title: Direct copy from ticket title
      computedTitle = ticketTitle;
      
      // Description format for Navigator template
      computedDescription = [
        "Requestor",
        `- Email: ${submitterEmail || "N/A"}`,
        "",
        "Created By",
        `- Team Member: ${assignedToName || "N/A"}`,
        "",
        "Module",
        `- ${ticketModule || "N/A"}`,
      ].join("\n");
      
      // Acceptance Criteria from ticket issue description (2000 char max)
      computedAcceptanceCriteria = (ticketIssueDescription || "").substring(0, 2000);
      
      // Template constants
      computedWorkItemType = SYSTEM_ISSUE_NAVIGATOR_TEMPLATE.workItemType;
      computedAdoProject = SYSTEM_ISSUE_NAVIGATOR_TEMPLATE.adoProject;
      
      setWorkItemType(computedWorkItemType);
      setAdoProject(computedAdoProject);
      setAdoBoard(SYSTEM_ISSUE_NAVIGATOR_TEMPLATE.adoBoard);
      setAcceptanceCriteria(computedAcceptanceCriteria);
      setTargetDate("");
      setAssignedToAdo("");
      setTags("");
      setEnvironment("");
      setBugSeverity("");
      
    // Template-specific logic for System Enhancement - Navigator
    } else if (isSystemEnhancementNavigatorTemplate) {
      // Title: Direct copy from ticket title
      computedTitle = ticketTitle;
      
      // Description format for Navigator template
      computedDescription = [
        "Requestor",
        `- Email: ${submitterEmail || "N/A"}`,
        "",
        "Created By",
        `- Team Member: ${assignedToName || "N/A"}`,
        "",
        "Module",
        `- ${ticketModule || "N/A"}`,
      ].join("\n");
      
      // Acceptance Criteria from ticket issue description (2000 char max)
      computedAcceptanceCriteria = (ticketIssueDescription || "").substring(0, 2000);
      
      // Template constants
      computedWorkItemType = SYSTEM_ENHANCEMENT_NAVIGATOR_TEMPLATE.workItemType;
      computedAdoProject = SYSTEM_ENHANCEMENT_NAVIGATOR_TEMPLATE.adoProject;
      
      setWorkItemType(computedWorkItemType);
      setAdoProject(computedAdoProject);
      setAdoBoard(SYSTEM_ENHANCEMENT_NAVIGATOR_TEMPLATE.adoBoard);
      setAcceptanceCriteria(computedAcceptanceCriteria);
      setTargetDate("");
      setAssignedToAdo("");
      setTags("");
      setEnvironment("");
      setBugSeverity("");
      
    // Template-specific logic for Reporting templates (New, Enhancement, or Fix)
    } else if (isReportingTemplate && reportingTemplate) {
      // Title: Direct copy from ticket title
      computedTitle = ticketTitle;
      
      // Description format for Reporting template
      computedDescription = [
        "Source Ticket",
        `- Ticket: ${primaryTicketId || "N/A"}`,
        `- Category: ${adoCategory || "N/A"}`,
        `- Sub-Category: ${adoSubcategory || "N/A"}`,
        "",
        "Requestor",
        `- Email: ${submitterEmail || "N/A"}`,
        "",
        "Created By",
        `- Team Member: ${assignedToName || "N/A"}`,
        "",
        "Dashboard Link",
        `- ${dashboardLink || "N/A"}`,
      ].join("\n");
      
      // Acceptance Criteria from ticket description (2000 char max)
      computedAcceptanceCriteria = (adoDescription || "").substring(0, 2000);
      
      // Template constants
      computedWorkItemType = reportingTemplate.workItemType;
      // computedAdoProject = "Reporting Platform";
      
      setWorkItemType(computedWorkItemType);
      setAdoProject("Analytics");
      setAdoBoard("Analytics");
      setAssignedToAdo(reportingTemplate.assignedTo);
      setTags(reportingTemplate.tags);
      setAcceptanceCriteria(computedAcceptanceCriteria);
      setTargetDate("");
    } else {
      // Default template for other category/subcategory combinations
      computedTitle = primaryTicketId
        ? `[${primaryTicketId}] ${ticketTitle}`
        : ticketTitle;
      computedDescription = [
        "Source Ticket",
        primaryTicketId ? `- Ticket: ${primaryTicketId}` : "- Ticket: N/A",
        adoCategory ? `- Category: ${adoCategory}` : "- Category: N/A",
        adoSubcategory ? `- Sub-Category: ${adoSubcategory}` : "- Sub-Category: N/A",
        "",
        "Issue Details",
        adoDescription?.trim() || "Please add reproduction steps.",
      ].join("\n");
      
      setAdoProject(ADO_PROJECT_OPTIONS[0]);
      setWorkItemType(WORK_ITEM_TYPE_OPTIONS[0]);
      setAcceptanceCriteria("");
      setTargetDate("");
      setAdoBoard("Analytics");
      setAssignedToAdo("");
      setTags("");
    }

    setTitle(computedTitle);
    setDescription(computedDescription);
    
    // Use ticket priority if available, otherwise use default (or fixed default for Bug Review)
    let defaultPriority = mapUrgencyToAdoPriority(ticketPriority) || PRIORITY_OPTIONS[0];
    if (isSystemIssueBugReviewTemplate) {
      defaultPriority = "3 - Medium";
    }
    setPriority(defaultPriority);
    
    // Use ticket severity (urgency) if available, otherwise use default
    // For WES templates, ensure we use a valid option from WES_SEVERITY_OPTIONS
    let defaultSeverity = mapUrgencyToAdoPriority(ticketPriority) || (isSystemIssueVisionPickTemplate || isSystemIssueBugReviewTemplate ? WES_SEVERITY_OPTIONS[0] : SEVERITY_OPTIONS[0]);
    // If we got a severity that's not valid for this template, use the first option for the template
    const applicableSeverityOptions = (isSystemIssueVisionPickTemplate || isSystemIssueBugReviewTemplate) ? WES_SEVERITY_OPTIONS : SEVERITY_OPTIONS;
    if (!applicableSeverityOptions.includes(defaultSeverity)) {
      defaultSeverity = applicableSeverityOptions[2];
    }
    setSeverity(defaultSeverity);
    
    setTouched({});
    setErrors({});

    initialState.current = {
      adoProject: isReportingTemplate ? "Reporting Platform" : (isSystemIssueVisionPickTemplate || isSystemIssueBugReviewTemplate ? "WES" : ((isSystemIssueNavigatorTemplate || isSystemEnhancementNavigatorTemplate) ? "Navigator" : ADO_PROJECT_OPTIONS[0])),
      workItemType: (isReportingTemplate && reportingTemplate) ? reportingTemplate.workItemType : (isSystemIssueVisionPickTemplate ? "Bug" : (isSystemIssueBugReviewTemplate ? "Bug" : ((isSystemIssueNavigatorTemplate || isSystemEnhancementNavigatorTemplate) ? "User Story" : WORK_ITEM_TYPE_OPTIONS[0]))),
      priority: defaultPriority,
      severity: defaultSeverity,
      title: computedTitle,
      description: computedDescription,
      acceptanceCriteria: computedAcceptanceCriteria || "",
      targetDate: "",
      adoBoard: isReportingTemplate ? "Analytics" : (isSystemIssueVisionPickTemplate ? "WES Bug Review" : ((isSystemIssueNavigatorTemplate || isSystemEnhancementNavigatorTemplate) ? "Navigator Board" : "Analytics")),
      assignedToAdo: isReportingTemplate && reportingTemplate ? reportingTemplate.assignedTo : "",
      tags: isReportingTemplate && reportingTemplate ? reportingTemplate.tags : "",
    };
  }, [adoOpen, primaryTicketId, primaryTicketTitle, adoCategory, adoSubcategory, adoDescription, submitterEmail, assignedToName, dashboardLink, ticketPriority, ticketVersion, ticketSite, ticketWesEquipment, ticketDateTimeStamp, ticketPreconditions, ticketStepsToReproduce, ticketExpectedResults, ticketCurrentResults, ticketModule, ticketIssueDescription, isReportingTemplate, reportingTemplate, isSystemIssueVisionPickTemplate, isSystemIssueBugReviewTemplate, isSystemIssueNavigatorTemplate, isSystemEnhancementNavigatorTemplate, selectedLocation, appLocations, ticketDetails]);

  function validateField(field: string, value: string) {
    // wesVersion is optional, so don't validate it as required
    if (field === "wesVersion") return "";
    
    if (!value || value.trim() === "") {
      return `${field} is required for ADO submission.`;
    }
    return "";
  }

  function validateAll() {
    const newErrors: { [k: string]: string } = {};
    if (!adoProject) newErrors.adoProject = "ADO Project is required for ADO submission.";
    if (!workItemType) newErrors.workItemType = "Work Item Type is required for ADO submission.";
    if (!priority) newErrors.priority = "Priority is required for ADO submission.";
    if (!severity) newErrors.severity = "Severity is required for ADO submission.";
    if (!title || title.trim() === "") newErrors.title = "Title is required for ADO submission.";
    
    // Vision Pick and Bug Review descriptions are assembled from individual fields at submit time
    if (!isSystemIssueVisionPickTemplate && !isSystemIssueBugReviewTemplate && (!description || description.trim() === "")) {
      newErrors.description = "Description is required for ADO submission.";
    }
    
    // Template-specific validation for Reporting templates
    if (isReportingTemplate) {
      if (!acceptanceCriteria || acceptanceCriteria.trim() === "") {
        newErrors.acceptanceCriteria = "Acceptance Criteria is required for this template.";
      }
      if (!targetDate || targetDate.trim() === "") {
        newErrors.targetDate = "Target Date is required for this template.";
      }
    }
    
    // Template-specific validation for System Issue - Navigator
    if (isSystemIssueNavigatorTemplate) {
      if (!acceptanceCriteria || acceptanceCriteria.trim() === "") {
        newErrors.acceptanceCriteria = "Acceptance Criteria is required for this template.";
      }
    }
    
    // Template-specific validation for System Enhancement - Navigator
    if (isSystemEnhancementNavigatorTemplate) {
      if (!acceptanceCriteria || acceptanceCriteria.trim() === "") {
        newErrors.acceptanceCriteria = "Acceptance Criteria is required for this template.";
      }
    }
    
    // Template-specific validation for Vision Pick and Bug Review (WES) — all individual fields required
    if (isSystemIssueVisionPickTemplate || isSystemIssueBugReviewTemplate) {
      if (!siteInput || siteInput.trim() === "") {
        newErrors.siteInput = "Site is required for this template.";
      }
      if (!locationId || locationId.trim() === "") {
        newErrors.locationId = "Location ID is required for this template.";
      }
      if (!wesEquipment || wesEquipment.trim() === "") {
        newErrors.wesEquipment = "WES Equipment is required for this template.";
      }
      if (!errorTimeWithTimezone || errorTimeWithTimezone.trim() === "") {
        newErrors.errorTimeWithTimezone = "Date / Time Stamp is required for this template.";
      }
      if (!preconditions || preconditions.trim() === "") {
        newErrors.preconditions = "Preconditions is required for this template.";
      }
      if (!stepsToReproduce || stepsToReproduce.trim() === "") {
        newErrors.stepsToReproduce = "Steps to Reproduce is required for this template.";
      }
      if (!expectedResult || expectedResult.trim() === "") {
        newErrors.expectedResult = "Expected Results is required for this template.";
      }
      if (!currentResult || currentResult.trim() === "") {
        newErrors.currentResult = "Current Results is required for this template.";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function isFormValid(): boolean {
    // Check basic required fields for all templates
    if (!adoProject || !workItemType || !priority || !severity || !title || title.trim() === "") {
      return false;
    }

    // Vision Pick and Bug Review descriptions are assembled from individual fields at submit time
    if (!isSystemIssueVisionPickTemplate && !isSystemIssueBugReviewTemplate && (!description || description.trim() === "")) {
      return false;
    }

    // Template-specific validation for Reporting templates
    if (isReportingTemplate) {
      if (!acceptanceCriteria || acceptanceCriteria.trim() === "") return false;
      if (!targetDate || targetDate.trim() === "") return false;
    }

    // Template-specific validation for System Issue - Navigator
    if (isSystemIssueNavigatorTemplate) {
      if (!acceptanceCriteria || acceptanceCriteria.trim() === "") return false;
    }

    // Template-specific validation for System Enhancement - Navigator
    if (isSystemEnhancementNavigatorTemplate) {
      if (!acceptanceCriteria || acceptanceCriteria.trim() === "") return false;
    }

    // Template-specific validation for Vision Pick and Bug Review (WES) — all individual fields required
    if (isSystemIssueVisionPickTemplate || isSystemIssueBugReviewTemplate) {
      if (!siteInput || siteInput.trim() === "") return false;
      if (!locationId || locationId.trim() === "") return false;
      if (!wesEquipment || wesEquipment.trim() === "") return false;
      if (!errorTimeWithTimezone || errorTimeWithTimezone.trim() === "") return false;
      if (!preconditions || preconditions.trim() === "") return false;
      if (!stepsToReproduce || stepsToReproduce.trim() === "") return false;
      if (!expectedResult || expectedResult.trim() === "") return false;
      if (!currentResult || currentResult.trim() === "") return false;
    }

    return true;
  }

  function handleBlur(field: string, value: string) {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const err = validateField(field.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()), value);
    setErrors((prev) => ({ ...prev, [field]: err }));
  }

  function isDirty() {
    return (
      adoProject !== initialState.current.adoProject ||
      workItemType !== initialState.current.workItemType ||
      priority !== initialState.current.priority ||
      severity !== initialState.current.severity ||
      title !== initialState.current.title ||
      description !== initialState.current.description ||
      acceptanceCriteria !== initialState.current.acceptanceCriteria ||
      targetDate !== initialState.current.targetDate ||
      adoBoard !== initialState.current.adoBoard ||
      assignedToAdo !== initialState.current.assignedToAdo ||
      tags !== initialState.current.tags
    );
  }

  // Build ADO description for Vision Pick / Bug Review from individual field state vars
  const buildVisionPickDescription = () => [
    "Requestor",
    `- Email: ${submitterEmail || "N/A"}`,
    "",
    "Created By",
    `- Team Member: ${assignedToName || "N/A"}`,
    "",
    "Version",
    `- ${wesVersion || "N/A"}`,
    "",
    "Site",
    `- ${siteInput || "N/A"}`,
    "",
    "Location ID",
    `- ${locationId || "N/A"}`,
    "",
    "WES Equipment",
    `- ${wesEquipment || "N/A"}`,
    "",
    "Date/Time Stamp",
    `- ${errorTimeWithTimezone || "N/A"}`,
    "",
    "Preconditions",
    `- ${preconditions || "N/A"}`,
    "",
    "Steps to Reproduce",
    `- ${stepsToReproduce || "N/A"}`,
    "",
    "Expected Results",
    `- ${expectedResult || "N/A"}`,
    "",
    "Current Results",
    `- ${currentResult || "N/A"}`,
    "",
    "Environment",
    `- ${environment || "N/A"}`,
    "",
    "Bug Severity",
    `- ${bugSeverity || "N/A"}`,
  ].join("\n");

  const handleAdoSubmit = async () => {
    if (isAdoSubmitting) return;
    if (!validateAll()) {
      Object.keys(initialState.current).forEach((k) => setTouched((prev) => ({ ...prev, [k]: true })));
      return;
    }
    setIsAdoSubmitting(true);
    setAdoSubmitError(null);
    try {
      const priorityNumber = Number(String(priority).split("-")[0].trim()) || 3;
      const descriptionText = (isSystemIssueVisionPickTemplate || isSystemIssueBugReviewTemplate)
        ? buildVisionPickDescription()
        : description;
      const reproStepsHtml = `<div>${descriptionText
        .split("\n")
        .map((line) => line.replace(/</g, "&lt;").replace(/>/g, "&gt;"))
        .join("<br>")}</div>`;
      const payload: any = {
        ticketId: primaryTicketId,  // CRITICAL: Include ticket ID so backend can link ADO work item to ticket
        project: adoProject,
        workItemType,
        title,
        reproStepsHtml,
        severity: severity,  // Send full formatted string (e.g., "2 - High")
        priority: priorityNumber,
      };
      if (isReportingTemplate) {
        payload.acceptanceCriteria = acceptanceCriteria;
        payload.targetDate = targetDate;
        payload.adoBoard = adoBoard;
        payload.assignedToAdo = assignedToAdo;
        payload.tags = tags;
        payload.isReportingTemplate = true;
      }
      if (isSystemIssueVisionPickTemplate) {
        payload.adoBoard = SYSTEM_ISSUE_VISION_PICK_TEMPLATE.adoBoard;
        payload.isSystemIssueVisionPickTemplate = true;
        payload.environment = environment;
        payload.locationId = locationId;
        payload.siteInput = siteInput;
        payload.wesVersion = wesVersion;
        payload.wesEquipment = wesEquipment;
        payload.errorTimeWithTimezone = errorTimeWithTimezone;
        payload.preconditions = preconditions;
        payload.stepsToReproduce = stepsToReproduce;
        payload.expectedResult = expectedResult;
        payload.currentResult = currentResult;
        payload.bugSeverity = bugSeverity;
      }
      if (isSystemIssueBugReviewTemplate) {
        payload.adoBoard = SYSTEM_ISSUE_BUG_REVIEW_TEMPLATE.adoBoard;
        payload.isSystemIssueBugReviewTemplate = true;
        payload.environment = environment;
        payload.locationId = locationId;
        payload.siteInput = siteInput;
        payload.wesVersion = wesVersion;
        payload.wesEquipment = wesEquipment;
        payload.errorTimeWithTimezone = errorTimeWithTimezone;
        payload.preconditions = preconditions;
        payload.stepsToReproduce = stepsToReproduce;
        payload.expectedResult = expectedResult;
        payload.currentResult = currentResult;
        payload.bugSeverity = bugSeverity;
      }
      if (isSystemIssueNavigatorTemplate) {
        payload.acceptanceCriteria = acceptanceCriteria;
        payload.adoBoard = SYSTEM_ISSUE_NAVIGATOR_TEMPLATE.adoBoard;
        payload.isSystemIssueNavigatorTemplate = true;
      }
      if (isSystemEnhancementNavigatorTemplate) {
        payload.acceptanceCriteria = acceptanceCriteria;
        payload.adoBoard = SYSTEM_ENHANCEMENT_NAVIGATOR_TEMPLATE.adoBoard;
        payload.isSystemEnhancementNavigatorTemplate = true;
      }
      const result = await onConfirmAdo(payload);
      if (result.type === "error") {
        setAdoSubmitError(result.msg);
        setAdoRetryCount((prev) => prev + 1);
      }
    } catch (err: any) {
      setAdoSubmitError(err?.message || "ADO submission failed. Please review your entries and try again.");
      setAdoRetryCount((prev) => prev + 1);
    } finally {
      setIsAdoSubmitting(false);
    }
  };

  return (
    <>
      <Modal visible={mergeOpen} transparent animationType="fade" onRequestClose={onCloseMerge}>
        <View style={styles.detailModalOverlay}>
          <View style={[styles.detailModalCard, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
            <View style={[styles.detailModalHeader, isDark && { borderBottomColor: colors.border.divider }]}>
              <View style={styles.detailModalTitleRow}>
                <MaterialIcons name="merge-type" size={18} color="#034895" />
                <Text style={[styles.detailModalTitle, isDark && { color: colors.text.primary }]}>Merge Duplicate Tickets</Text>
              </View>
              <Pressable onPress={onCloseMerge} style={styles.detailModalCloseBtn}>
                <MaterialIcons name="close" size={18} color={isDark ? "#B0B0B0" : "#3A3A3A"} />
              </Pressable>
            </View>

            <ScrollView style={styles.detailModalBody} keyboardShouldPersistTaps="handled">
              <Text style={[styles.detailModalBodyText, isDark && { color: colors.text.secondary }]}>
                Merging tickets consolidates submitter information, comments, attachments, and impact data into the
                primary ticket. All original submitters and stakeholders will be notified.
              </Text>

              <Text style={styles.detailModalLabel}>Primary Ticket (will remain active)</Text>
              <View style={styles.detailModalTicketCardPrimary}>
                <Text style={styles.detailModalTicketId}>{primaryTicketId ?? "—"}</Text>
                <Text style={styles.detailModalTicketTitle}>{primaryTicketTitle ?? "—"}</Text>
              </View>

              {/* ── Selected for Merge list ─────────────────────────────── */}
              <View style={mergeStyles.selectedHeader}>
                <Text style={styles.detailModalLabel}>
                  {"Selected for Merge "}
                  <Text style={mergeStyles.selectedCount}>({selectedCount} selected)</Text>
                </Text>
              </View>

              {selectedCount === 0 ? (
                <View style={mergeStyles.emptySelected}>
                  <Text style={mergeStyles.emptySelectedText}>No tickets selected yet. Search below to add.</Text>
                </View>
              ) : (
                selectedTickets.map((t) => (
                  <View key={t.id} style={mergeStyles.selectedCard}>
                    <View style={mergeStyles.selectedCardInfo}>
                      <Text style={styles.detailModalTicketId}>{t.id}</Text>
                      <Text style={mergeStyles.selectedCardTitle} numberOfLines={2}>{t.title}</Text>
                      <View style={mergeStyles.selectedCardMeta}>
                        {t.submittedBy ? (
                          <Text style={mergeStyles.selectedMetaItem}>
                            <Text style={mergeStyles.selectedMetaLabel}>Submitter: </Text>
                            {t.submittedBy}
                          </Text>
                        ) : null}
                        <Text style={mergeStyles.selectedMetaItem}>
                          <Text style={mergeStyles.selectedMetaLabel}>Location: </Text>
                          {t.location}
                        </Text>
                      </View>
                    </View>
                    <Pressable
                      onPress={() => removeFromMerge(t.id)}
                      style={({ pressed }) => [mergeStyles.removeBtn, pressed && { opacity: 0.6 }]}
                      accessibilityLabel={`Remove ${t.id} from merge`}
                    >
                      <MaterialIcons name="close" size={16} color="#C12C01" />
                    </Pressable>
                  </View>
                ))
              )}

              {/* ── Search ─────────────────────────────────────────────── */}
              <Text style={[styles.detailModalLabel, { marginTop: 14 }]}>Search Tickets to Absorb</Text>

              <View style={mergeStyles.searchRow}>
                <MaterialIcons name="search" size={16} color="#6b7280" style={mergeStyles.searchIcon} />
                <TextInput
                  style={mergeStyles.searchInput}
                  value={mergeSearchQuery}
                  onChangeText={setMergeSearchQuery}
                  placeholder="Search by Ticket ID, title, or keywords"
                  placeholderTextColor="#9099aa"
                  returnKeyType="search"
                  autoCorrect={false}
                />
                {mergeSearchQuery.length > 0 && (
                  <Pressable onPress={() => setMergeSearchQuery("")} style={mergeStyles.searchClear}>
                    <MaterialIcons name="close" size={14} color="#6b7280" />
                  </Pressable>
                )}
              </View>

              {mergeSearchQuery.trim() === "" && (
                <Text style={mergeStyles.searchHint}>
                  Type to search for open tickets to absorb into this one.
                </Text>
              )}

              {isSearching && (
                <Text style={mergeStyles.searchHint}>Searching…</Text>
              )}
              {mergeSearchQuery.trim() !== "" && !isSearching && mergeSearchResults.length === 0 && (
                <Text style={mergeStyles.searchHint}>No matching tickets found.</Text>
              )}

              {/* Search results */}
              {mergeSearchResults.map((t) => {
                // t.ticketNumber, t.title, t.status, t.category, t.subCategory, t.submitterEmail, t.levelReferenceId
                const statusKey = normalizeMergeStatus(t.status);
                const eligible = isMergeable(statusKey);
                const isDisabled = !eligible;
                const tooltipText = !eligible
                  ? mergeDisabledTooltip(statusKey)
                  : "";

                return (
                  <View
                    key={t.ticketNumber}
                    style={[
                      mergeStyles.resultCard,
                      !eligible && mergeStyles.resultCardDisabled,
                    ]}
                    // @ts-ignore — title renders as HTML tooltip on web
                    {...(Platform.OS === "web" && isDisabled ? { title: tooltipText } : {})}
                  >
                    <View style={mergeStyles.resultCardLeft}>
                      <View style={mergeStyles.resultTopRow}>
                        <Text
                          style={[
                            styles.detailModalTicketId,
                            !eligible && mergeStyles.disabledText,
                          ]}
                        >
                          {t.ticketNumber}
                        </Text>
                        <View
                          style={[
                            mergeStyles.statusChip,
                            { backgroundColor: !eligible ? "#EBEBEB" : (MERGE_STATUS_BG[statusKey] ?? "#f0f0f0") },
                          ]}
                        >
                          <Text
                            style={[
                              mergeStyles.statusChipText,
                              { color: !eligible ? "#818181" : (MERGE_STATUS_COLOR[statusKey] ?? "#555") },
                            ]}
                          >
                            {MERGE_STATUS_LABEL[statusKey] ?? t.status}
                          </Text>
                        </View>
                      </View>
                      <Text
                        style={[
                          styles.detailModalTicketTitle,
                          !eligible && mergeStyles.disabledText,
                        ]}
                        numberOfLines={2}
                      >
                        {t.title}
                      </Text>
                      <View style={mergeStyles.resultMetaGrid}>
                        <Text style={[mergeStyles.resultMetaItem, !eligible && mergeStyles.disabledText]}>
                          <Text style={mergeStyles.resultMetaLabel}>Category: </Text>
                          {t.category}{t.subCategory ? ` › ${t.subCategory}` : ""}
                        </Text>
                        {t.submitterEmail ? (
                          <Text style={[mergeStyles.resultMetaItem, !eligible && mergeStyles.disabledText]}>
                            <Text style={mergeStyles.resultMetaLabel}>Submitter: </Text>
                            {t.submitterEmail}
                          </Text>
                        ) : null}
                        <Text style={[mergeStyles.resultMetaItem, !eligible && mergeStyles.disabledText]}>
                          <Text style={mergeStyles.resultMetaLabel}>Location: </Text>
                          {t.levelReferenceId}
                        </Text>
                        <Text style={[mergeStyles.resultMetaItem, !eligible && mergeStyles.disabledText]}>
                          <Text style={mergeStyles.resultMetaLabel}>Status: </Text>
                          {MERGE_STATUS_LABEL[statusKey] ?? t.status}
                        </Text>
                      </View>
                      {!eligible && (
                        <View style={mergeStyles.ineligibleNote}>
                          <MaterialIcons name="info-outline" size={12} color="#818181" />
                          <Text style={mergeStyles.ineligibleNoteText}>{mergeDisabledTooltip(statusKey)}</Text>
                        </View>
                      )}
                    </View>
                    <Pressable
                      onPress={isDisabled ? undefined : () => addToMerge(t)}
                      style={({ pressed }) => [
                        mergeStyles.addBtn,
                        isDisabled && mergeStyles.addBtnDisabled,
                        !isDisabled && pressed && { opacity: 0.7 },
                      ]}
                      disabled={isDisabled}
                      // @ts-ignore — title for web tooltip
                      {...(Platform.OS === "web" && isDisabled ? { title: tooltipText } : {})}
                    >
                      <MaterialIcons
                        name="add"
                        size={14}
                        color={isDisabled ? "#B0B0B0" : "#034895"}
                      />
                      <Text style={[mergeStyles.addBtnText, isDisabled && mergeStyles.addBtnTextDisabled]}>
                        Add to Merge
                      </Text>
                    </Pressable>
                  </View>
                );
              })}

              <Text style={styles.detailModalLabel}>Reason for Merge</Text>
              <Text style={styles.detailModalOptional}>optional, visible on activity stream</Text>
              <TextInput
                style={styles.detailModalInput}
                placeholder="Describe why these tickets are duplicates…"
                placeholderTextColor="#9099aa"
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />

              {/* ── Merge Impact Summary ────────────────────────────────── */}
              {impactSummary && (
                <View style={mergeStyles.impactCard}>
                  <View style={mergeStyles.impactHeader}>
                    <MaterialIcons name="summarize" size={15} color="#034895" />
                    <Text style={mergeStyles.impactHeaderText}>Merge Impact Summary</Text>
                  </View>

                  {/* Submitters */}
                  <View style={mergeStyles.impactRow}>
                    <MaterialIcons name="person" size={14} color="#6b7280" />
                    <Text style={mergeStyles.impactRowText}>
                      <Text style={mergeStyles.impactRowLabel}>Submitters: </Text>
                      {impactSummary.additionalSubmittersCount > 0
                        ? `Primary submitter + ${impactSummary.additionalSubmittersCount} additional submitter${impactSummary.additionalSubmittersCount > 1 ? "s" : ""}`
                        : "Primary submitter only"}
                    </Text>
                  </View>

                  {/* Locations */}
                  <View style={mergeStyles.impactRow}>
                    <MaterialIcons name="location-on" size={14} color="#6b7280" />
                    <Text style={mergeStyles.impactRowText}>
                      <Text style={mergeStyles.impactRowLabel}>Locations affected: </Text>
                      {`${impactSummary.totalLocations} location${impactSummary.totalLocations !== 1 ? "s" : ""} (combined from all tickets)`}
                    </Text>
                  </View>

                  {/* Urgency */}
                  <View style={mergeStyles.impactRow}>
                    <MaterialIcons name="priority-high" size={14} color="#6b7280" />
                    <Text style={mergeStyles.impactRowText}>
                      <Text style={mergeStyles.impactRowLabel}>Urgency: </Text>
                      {`Will be set to `}
                      <Text style={[mergeStyles.impactUrgency, { color: MERGE_STATUS_COLOR[impactSummary.highestUrgency] ?? "#555" }]}>
                        {toTitleCaseWord(impactSummary.highestUrgency)}
                      </Text>
                      {impactSummary.urgencyRaised ? " (raised from absorbed ticket)" : ""}
                    </Text>
                  </View>

                  {/* ServiceNow */}
                  <View style={mergeStyles.impactRow}>
                    <MaterialIcons name="sync" size={14} color="#6b7280" />
                    <Text style={mergeStyles.impactRowText}>
                      <Text style={mergeStyles.impactRowLabel}>ServiceNow: </Text>
                      {impactSummary.primaryIncident
                        ? `Primary ServiceNow link maintained (${impactSummary.primaryIncident})`
                        : "No ServiceNow conflicts"}
                    </Text>
                  </View>
                </View>
              )}

              <View style={styles.detailModalWarningBox}>
                <MaterialIcons name="warning-amber" size={16} color="#8B6508" />
                <Text style={styles.detailModalWarningText}>
                  Merging is irreversible. Absorbed tickets become read-only and redirect users to the primary ticket.
                </Text>
              </View>

              {/* Inline validation error — shown when a selected ticket changed status while modal was open */}
              {mergeValidationError ? (
                <View style={mergeStyles.validationErrorBanner}>
                  <MaterialIcons name="error-outline" size={15} color="#C12C01" />
                  <Text style={mergeStyles.validationErrorText}>{mergeValidationError}</Text>
                </View>
              ) : null}
            </ScrollView>

            <View style={styles.detailModalFooter}>
              <Pressable style={styles.btnGhost} onPress={onCloseMerge}>
                <Text style={styles.btnGhostText}>Cancel</Text>
              </Pressable>
              <Pressable
                style={[styles.btnPrimary, selectedCount === 0 && mergeStyles.btnDisabled]}
                onPress={selectedCount > 0 ? () => {
                  // Frontend-only merge flow: validate against the status captured at selection time.
                  const ineligible = selectedIds.filter((id) => {
                    const status = normalizeMergeStatus(selectedTicketSnapshot[id]?.status);
                    return !!status && !isMergeable(status);
                  });
                  if (ineligible.length > 0) {
                    setMergeValidationError(
                      "One or more selected tickets are no longer eligible for merge. Please refresh and reselect."
                    );
                    return;
                  }
                  setMergeValidationError(null);
                  setShowConfirmDialog(true);
                } : undefined}
              >
                <Text style={styles.btnPrimaryText}>
                  {selectedCount > 0
                    ? `Confirm Merge (${selectedCount} ticket${selectedCount > 1 ? "s" : ""})`
                    : "Confirm Merge"}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Final Merge Confirmation Dialog ───────────────────────────── */}
      <Modal
        visible={showConfirmDialog}
        transparent
        animationType="fade"
        onRequestClose={() => setShowConfirmDialog(false)}
      >
        <View style={confirmStyles.overlay}>
          <View style={confirmStyles.card}>
            <View style={confirmStyles.iconRow}>
              <View style={confirmStyles.iconWrap}>
                <MaterialIcons name="merge-type" size={28} color="#034895" />
              </View>
            </View>
            <Text style={confirmStyles.title}>
              {`Merge ${selectedCount} ticket${selectedCount !== 1 ? "s" : ""} into ${primaryTicketId ?? "primary"}?`}
            </Text>
            <Text style={confirmStyles.body}>
              This action cannot be undone. All absorbed tickets will be closed and their submitters will be notified.
            </Text>
            {impactSummary?.primaryIncident ? (
              <View style={confirmStyles.snConflictBox}>
                <MaterialIcons name="sync" size={15} color="#0078A7" />
                <Text style={confirmStyles.snConflictText}>
                  <Text style={{ fontWeight: "700" }}>ServiceNow: </Text>
                  {`Incident `}
                  <Text style={{ fontWeight: "700" }}>{impactSummary.primaryIncident}</Text>
                  {` will be maintained. Any ServiceNow links from absorbed tickets will be discarded.`}
                </Text>
              </View>
            ) : null}
            <View style={confirmStyles.footer}>
              <Pressable
                style={({ pressed }) => [confirmStyles.btnCancel, pressed && { opacity: 0.75 }, isMergeSubmitting && { opacity: 0.5 }]}
                onPress={() => setShowConfirmDialog(false)}
                disabled={isMergeSubmitting}
              >
                <Text style={confirmStyles.btnCancelText}>Cancel</Text>
              </Pressable>
              <Pressable
                style={({ pressed }) => [confirmStyles.btnMerge, pressed && { opacity: 0.75 }, isMergeSubmitting && { opacity: 0.6 }]}
                onPress={async () => {
                  setIsMergeSubmitting(true);
                  try {
                    await onConfirmMerge(selectedIds);
                    setShowConfirmDialog(false);
                    // After a successful merge, check if total aggregated locations >= 5
                    // and prompt the user to flag as Widespread
                    if (onFlagWidespread && impactSummary && impactSummary.totalLocations >= 5) {
                      setShowWidespreadPrompt(true);
                    }
                  } catch (err) {
                    console.error('[TicketDetailActionModals] Merge failed:', err);
                    // Modal stays open on error so user can retry
                  } finally {
                    setIsMergeSubmitting(false);
                  }
                }}
                disabled={isMergeSubmitting}
              >
                {isMergeSubmitting ? (
                  <>
                    <ActivityIndicator size="small" color="#ffffff" style={{ marginRight: 6 }} />
                    <Text style={confirmStyles.btnMergeText}>Merging...</Text>
                  </>
                ) : (
                  <>
                    <MaterialIcons name="merge-type" size={15} color="#ffffff" />
                    <Text style={confirmStyles.btnMergeText}>Merge Tickets</Text>
                  </>
                )}
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Widespread Flag Prompt (shown after merge when total locations >= 5) ── */}
      <Modal
        visible={showWidespreadPrompt}
        transparent
        animationType="fade"
        onRequestClose={() => setShowWidespreadPrompt(false)}
      >
        <View style={confirmStyles.overlay}>
          <View style={confirmStyles.card}>
            <View style={confirmStyles.iconRow}>
              <View style={[confirmStyles.iconWrap, { backgroundColor: "#FFF3E0" }]}>
                <MaterialIcons name="warning" size={28} color="#CC6E16" />
              </View>
            </View>
            <Text style={confirmStyles.title}>Flag as Widespread Issue?</Text>
            <Text style={confirmStyles.body}>
              {`The merged ticket now has ${impactSummary?.totalLocations ?? "5+"} affected locations. Would you like to flag this as a Widespread incident? This will create an automatic banner visible to all affected users.`}
            </Text>
            <View style={confirmStyles.footer}>
              <Pressable
                style={({ pressed }) => [confirmStyles.btnCancel, pressed && { opacity: 0.75 }]}
                onPress={() => setShowWidespreadPrompt(false)}
              >
                <Text style={confirmStyles.btnCancelText}>No, Skip</Text>
              </Pressable>
              <Pressable
                style={({ pressed }) => [confirmStyles.btnMerge, { backgroundColor: "#CC6E16" }, pressed && { opacity: 0.75 }]}
                onPress={() => {
                  setShowWidespreadPrompt(false);
                  onFlagWidespread?.();
                }}
              >
                <MaterialIcons name="warning" size={15} color="#ffffff" />
                <Text style={confirmStyles.btnMergeText}>Yes, Flag Widespread</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={adoOpen} transparent animationType="fade" onRequestClose={() => {
        if (isDirty()) setShowDiscardConfirm(true);
        else onCloseAdo();
      }}>
        <View style={styles.detailModalOverlay}>
          <View style={[styles.detailModalCard, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
            <View style={[styles.detailModalHeader, isDark && { borderBottomColor: colors.border.divider }]}>
              <View style={styles.detailModalTitleRow}>
                <MaterialIcons name="bug-report" size={18} color="#034895" />
                <Text style={[styles.detailModalTitle, isDark && { color: colors.text.primary }]}>Generate ADO Work Item</Text>
              </View>
              <Text style={{ fontSize: 12, color: isDark ? colors.text.secondary : "#6b7280", marginTop: 3, paddingLeft: 26 }}>
                {`Creating ${workItemType || "Work Item"} for ADO Board: ${adoBoardDisplay}`}
              </Text>
              <Pressable
                onPress={() => {
                  if (isDirty()) setShowDiscardConfirm(true);
                  else onCloseAdo();
                }}
                style={styles.detailModalCloseBtn}
                accessibilityLabel="Close"
              >
                <MaterialIcons name="close" size={18} color={isDark ? "#B0B0B0" : "#3A3A3A"} />
              </Pressable>
            </View>

            <ScrollView style={styles.detailModalBody}>
              <View style={styles.detailModalSuccessBox}>
                <MaterialIcons name="check-circle" size={16} color="#027C06" />
                <Text style={styles.detailModalSuccessText}>
                  <Text style={{ fontWeight: "700" }}>Eligible: </Text>
                  {`Category "${adoCategory ?? "—"}" / Sub-Category "${adoSubcategory ?? "—"}" qualifies for ADO work item creation. Fields below are pre-populated from the ticket.`}
                </Text>
              </View>

              {/* Error banner displayed on submission failure */}
              {adoSubmitError && (
                <View style={{
                  flexDirection: "row", alignItems: "flex-start", gap: 8,
                  backgroundColor: "#FFF0EE", borderWidth: 1, borderColor: "#F5C2BB",
                  borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, marginBottom: 12,
                }}>
                  <MaterialIcons name="error-outline" size={15} color="#C12C01" style={{ marginTop: 1 }} />
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 13, color: "#C12C01", fontWeight: "600", lineHeight: 19 }}>
                      {adoRetryCount >= 2
                        ? "ADO submission failed after multiple attempts. Please contact system support or create the item manually in ADO."
                        : "ADO submission failed. Please review your entries and try again."}
                    </Text>
                  </View>
                  {adoRetryCount < 2 && (
                    <Pressable
                      onPress={handleAdoSubmit}
                      style={{ paddingHorizontal: 10, paddingVertical: 6, backgroundColor: "#C12C01", borderRadius: 6 }}
                    >
                      <Text style={{ color: "#fff", fontSize: 12, fontWeight: "700" }}>Retry Submission</Text>
                    </Pressable>
                  )}
                </View>
              )}

              <View style={styles.detailModalFormGrid}>
                <View style={[styles.detailModalFormField, { zIndex: 60 }]}> 
                  <Text style={styles.detailModalLabel}>ADO Board *</Text>
                  {(isReportingTemplate || isSystemIssueNavigatorTemplate || isSystemEnhancementNavigatorTemplate || isSystemIssueVisionPickTemplate) ? (
                    <TextInput
                      style={[styles.detailModalInputSingle, { backgroundColor: "#f5f5f5", color: "#666" }]}
                      value={adoBoard}
                      editable={false}
                    />
                  ) : (
                    <AdoDropdown
                      value={adoProject}
                      options={ADO_PROJECT_OPTIONS}
                      onChange={(v) => { setAdoProject(v); handleBlur("adoProject", v); }}
                      openKey="adoProject"
                      currentOpenKey={openDropdown}
                      setOpenKey={setOpenDropdown}
                    />
                  )}
                  {!!errors.adoProject && touched.adoProject && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginTop: 2 }}>{errors.adoProject}</Text>
                  )}
                </View>
                <View style={[styles.detailModalFormField, { zIndex: 50 }]}> 
                  <Text style={styles.detailModalLabel}>Work Item Type *</Text>
                  {(isReportingTemplate || isSystemIssueNavigatorTemplate || isSystemEnhancementNavigatorTemplate || isSystemIssueVisionPickTemplate) ? (
                    <TextInput
                      style={[styles.detailModalInputSingle, { backgroundColor: "#f5f5f5", color: "#666" }]}
                      value={workItemType}
                      editable={false}
                    />
                  ) : (
                    <AdoDropdown
                      value={workItemType}
                      options={WORK_ITEM_TYPE_OPTIONS}
                      onChange={(v) => { setWorkItemType(v); handleBlur("workItemType", v); }}
                      openKey="workItemType"
                      currentOpenKey={openDropdown}
                      setOpenKey={setOpenDropdown}
                    />
                  )}
                  {!!errors.workItemType && touched.workItemType && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginTop: 2 }}>{errors.workItemType}</Text>
                  )}
                </View>
              </View>


              <Text style={styles.detailModalLabel}>Title *</Text>
              <TextInput
                style={styles.detailModalInputSingle}
                value={title}
                onChangeText={(v) => { setTitle(v); handleBlur("title", v); }}
                onBlur={() => handleBlur("title", title)}
              />
              <Text style={{ fontSize: 12, color: "#6b7280", marginTop: 4, marginBottom: 8, fontStyle: "italic" }}>
                Elaborate on the Title name with appropriate details
              </Text>
              {!!errors.title && touched.title && (
                <Text style={{ color: "#C12C01", fontSize: 11, marginTop: 2 }}>{errors.title}</Text>
              )}


              <Text style={styles.detailModalLabel}>Description *</Text>
              {/* Vision Pick / Bug Review: description is assembled from individual fields below */}
              {!isSystemIssueVisionPickTemplate && !isSystemIssueBugReviewTemplate ? (
                <>
                  <TextInput
                    style={styles.detailModalInputLarge}
                    value={description}
                    onChangeText={(v) => { setDescription(v); handleBlur("description", v); }}
                    onBlur={() => handleBlur("description", description)}
                    multiline
                    numberOfLines={8}
                    textAlignVertical="top"
                    editable={!isAdoSubmitting}
                  />
                  {!!errors.description && touched.description && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginTop: 2 }}>{errors.description}</Text>
                  )}
                </>
              ) : (
                /* Vision Pick (WES) / Bug Review: individual editable description fields */
                <>
                  <Text style={{ fontSize: 11, color: "#9099aa", marginBottom: 10, fontStyle: "italic" }}>
                    These fields are assembled into the ADO Description. Edit as needed before submitting.
                  </Text>
                  <Text style={styles.detailModalLabel}>Version</Text>
                  <TextInput style={[styles.detailModalInputSingle, { marginBottom: 4 }]} value={wesVersion} onChangeText={(v) => { setWesVersion(v); }} editable={!isAdoSubmitting} placeholder="2.20260622" placeholderTextColor="#9099aa" />
                  {!!errors.wesVersion && touched.wesVersion && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginBottom: 8 }}>{errors.wesVersion}</Text>
                  )}
                  <Text style={styles.detailModalLabel}>Site *</Text>
                  <View style={[styles.detailModalFormField, { zIndex: 50, marginBottom: 4 }]}>
                    <AdoDropdown
                      value={siteInput}
                      options={appLocations?.map((loc) => loc.name) || []}
                      onChange={(selectedName) => {
                        setSiteInput(selectedName);
                        const matchedLoc = appLocations?.find((loc) => loc.name === selectedName);
                        setLocationId(matchedLoc?.id || "");
                        handleBlur("siteInput", selectedName);
                      }}
                      openKey="site"
                      currentOpenKey={openDropdown}
                      setOpenKey={setOpenDropdown}
                    />
                  </View>
                  {!!errors.siteInput && touched.siteInput && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginBottom: 8 }}>{errors.siteInput}</Text>
                  )}
                  <Text style={styles.detailModalLabel}>Location ID *</Text>
                  <TextInput style={[styles.detailModalInputSingle, { marginBottom: 4 }]} value={locationId} onChangeText={(v) => { setLocationId(v); handleBlur("locationId", v); }} onBlur={() => handleBlur("locationId", locationId)} editable={false} placeholder="Auto-populated from location" placeholderTextColor="#9099aa" />
                  {!!errors.locationId && touched.locationId && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginBottom: 8 }}>{errors.locationId}</Text>
                  )}
                  <Text style={styles.detailModalLabel}>WES Equipment *</Text>
                  <TextInput style={[styles.detailModalInputSingle, { marginBottom: 4 }]} value={wesEquipment} onChangeText={(v) => { setWesEquipment(v); handleBlur("wesEquipment", v); }} onBlur={() => handleBlur("wesEquipment", wesEquipment)} editable={!isAdoSubmitting} placeholder="e.g., RF Scanner" placeholderTextColor="#9099aa" />
                  {!!errors.wesEquipment && touched.wesEquipment && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginBottom: 8 }}>{errors.wesEquipment}</Text>
                  )}
                  <Text style={styles.detailModalLabel}>Date / Time Stamp *</Text>
                  <TextInput style={[styles.detailModalInputSingle, { marginBottom: 4 }]} value={errorTimeWithTimezone} onChangeText={(v) => { setErrorTimeWithTimezone(v); handleBlur("errorTimeWithTimezone", v); }} onBlur={() => handleBlur("errorTimeWithTimezone", errorTimeWithTimezone)} editable={!isAdoSubmitting} placeholder="e.g., 2026-06-19 10:30 AM PT" placeholderTextColor="#9099aa" />
                  {!!errors.errorTimeWithTimezone && touched.errorTimeWithTimezone && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginBottom: 8 }}>{errors.errorTimeWithTimezone}</Text>
                  )}
                  <Text style={styles.detailModalLabel}>Preconditions *</Text>
                  <TextInput style={[styles.detailModalInputLarge, { marginBottom: 4 }]} value={preconditions} onChangeText={(v) => { setPreconditions(v); handleBlur("preconditions", v); }} onBlur={() => handleBlur("preconditions", preconditions)} multiline numberOfLines={3} textAlignVertical="top" editable={!isAdoSubmitting} placeholder="Steps or conditions before the issue occurred" placeholderTextColor="#9099aa" />
                  {!!errors.preconditions && touched.preconditions && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginBottom: 8 }}>{errors.preconditions}</Text>
                  )}
                  <Text style={styles.detailModalLabel}>Steps to Reproduce *</Text>
                  <TextInput style={[styles.detailModalInputLarge, { marginBottom: 4 }]} value={stepsToReproduce} onChangeText={(v) => { setStepsToReproduce(v); handleBlur("stepsToReproduce", v); }} onBlur={() => handleBlur("stepsToReproduce", stepsToReproduce)} multiline numberOfLines={3} textAlignVertical="top" editable={!isAdoSubmitting} placeholder="1. Open the app..." placeholderTextColor="#9099aa" />
                  {!!errors.stepsToReproduce && touched.stepsToReproduce && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginBottom: 8 }}>{errors.stepsToReproduce}</Text>
                  )}
                  <Text style={styles.detailModalLabel}>Expected Results *</Text>
                  <TextInput style={[styles.detailModalInputLarge, { marginBottom: 4 }]} value={expectedResult} onChangeText={(v) => { setExpectedResult(v); handleBlur("expectedResult", v); }} onBlur={() => handleBlur("expectedResult", expectedResult)} multiline numberOfLines={3} textAlignVertical="top" editable={!isAdoSubmitting} placeholder="What should have happened" placeholderTextColor="#9099aa" />
                  {!!errors.expectedResult && touched.expectedResult && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginBottom: 8 }}>{errors.expectedResult}</Text>
                  )}
                  <Text style={styles.detailModalLabel}>Current Results *</Text>
                  <TextInput style={[styles.detailModalInputLarge, { marginBottom: 4 }]} value={currentResult} onChangeText={(v) => { setCurrentResult(v); handleBlur("currentResult", v); }} onBlur={() => handleBlur("currentResult", currentResult)} multiline numberOfLines={3} textAlignVertical="top" editable={!isAdoSubmitting} placeholder="What actually happened" placeholderTextColor="#9099aa" />
                  {!!errors.currentResult && touched.currentResult && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginBottom: 8 }}>{errors.currentResult}</Text>
                  )}
                  <View style={[styles.detailModalFormGrid, { marginTop: 4 }]}>
                    <View style={[styles.detailModalFormField, { zIndex: 18 }]}>
                      <Text style={styles.detailModalLabel}>Environment</Text>
                      <AdoDropdown value={environment} options={["Prod", "UAT", "QA"]} onChange={setEnvironment} openKey="env" currentOpenKey={openDropdown} setOpenKey={setOpenDropdown} />
                    </View>
                    <View style={[styles.detailModalFormField, { zIndex: 16 }]}>
                      <Text style={styles.detailModalLabel}>Bug Severity</Text>
                      <AdoDropdown value={bugSeverity} options={["1", "2", "3", "4", "5"]} onChange={setBugSeverity} openKey="bugSev" currentOpenKey={openDropdown} setOpenKey={setOpenDropdown} />
                    </View>
                  </View>
                </>
              )}

              {/* Template-specific fields for System Issue - Navigator and System Enhancement - Navigator */}
              {(isSystemIssueNavigatorTemplate || isSystemEnhancementNavigatorTemplate) && (
                <>
                  <Text style={styles.detailModalLabel}>Acceptance Criteria *</Text>
                  <TextInput
                    style={styles.detailModalInputLarge}
                    value={acceptanceCriteria}
                    onChangeText={(v) => { setAcceptanceCriteria(v); handleBlur("acceptanceCriteria", v); }}
                    onBlur={() => handleBlur("acceptanceCriteria", acceptanceCriteria)}
                    multiline
                    numberOfLines={4}
                    textAlignVertical="top"
                    placeholder="Enter acceptance criteria (max 2000 characters)"
                    placeholderTextColor="#999"
                  />
                  <Text style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                    {acceptanceCriteria.length}/2000 characters
                  </Text>
                  {!!errors.acceptanceCriteria && touched.acceptanceCriteria && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginTop: 2 }}>{errors.acceptanceCriteria}</Text>
                  )}
                </>
              )}

              {/* Template-specific fields for Reporting templates (New, Enhancement, or Fix) */}
              {isReportingTemplate && (
                <>
                  <View style={styles.detailModalFormGrid}>
                    <View style={[styles.detailModalFormField, { zIndex: 25 }]}> 
                      <Text style={styles.detailModalLabel}>ADO Board </Text>
                      <TextInput
                        style={[styles.detailModalInputSingle, { backgroundColor: "#f5f5f5", color: "#666" }]}
                        value={adoBoard}
                        editable={false}
                      />
                    </View>
                    <View style={[styles.detailModalFormField, { zIndex: 25 }]}> 
                      <Text style={styles.detailModalLabel}>Assigned To</Text>
                      <TextInput
                        style={[styles.detailModalInputSingle, { backgroundColor: "#f5f5f5", color: "#666" }]}
                        value={assignedToAdo}
                        editable={false}
                      />
                    </View>
                  </View>

                  <Text style={styles.detailModalLabel}>Acceptance Criteria *</Text>
                  <TextInput
                    style={styles.detailModalInputLarge}
                    value={acceptanceCriteria}
                    onChangeText={(v) => { 
                      // 2000 character limit
                      if (v.length <= 2000) {
                        setAcceptanceCriteria(v);
                        handleBlur("acceptanceCriteria", v);
                      }
                    }}
                    onBlur={() => handleBlur("acceptanceCriteria", acceptanceCriteria)}
                    multiline
                    numberOfLines={6}
                    textAlignVertical="top"
                    placeholder="Enter acceptance criteria (max 2000 characters)"
                    placeholderTextColor="#999"
                  />
                  <Text style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                    {acceptanceCriteria.length}/2000 characters
                  </Text>
                  {!!errors.acceptanceCriteria && touched.acceptanceCriteria && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginTop: 2 }}>{errors.acceptanceCriteria}</Text>
                  )}

                  <Text style={[styles.detailModalLabel, { marginTop: 16 }]}>Target Date *</Text>
                  {Platform.OS === "web" ? (
                    // @ts-ignore — HTML5 date input on web
                    <input
                      type="date"
                      value={targetDate}
                      onChange={(e: any) => {
                        const val = e.target.value;
                        setTargetDate(val);
                        handleBlur("targetDate", val);
                      }}
                      onBlur={() => handleBlur("targetDate", targetDate)}
                      style={{
                        width: "100%",
                        height: 36,
                        fontSize: 14,
                        fontWeight: "500",
                        paddingLeft: 10,
                        paddingRight: 10,
                        borderRadius: 7,
                        border: "1px solid #D0D0D0",
                        backgroundColor: "#ffffff",
                        color: "#2D2D2D",
                        boxSizing: "border-box",
                      } as any}
                    />
                  ) : (
                    <TextInput
                      style={styles.detailModalInputSingle}
                      value={targetDate}
                      onChangeText={(v) => { setTargetDate(v); handleBlur("targetDate", v); }}
                      onBlur={() => handleBlur("targetDate", targetDate)}
                      placeholder="YYYY-MM-DD"
                      placeholderTextColor="#999"
                    />
                  )}
                  {!!errors.targetDate && touched.targetDate && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginTop: 2 }}>{errors.targetDate}</Text>
                  )}

                  <View style={styles.detailModalFormGrid}>
                    <View style={[styles.detailModalFormField, { zIndex: 15 }]}> 
                      <Text style={styles.detailModalLabel}>Tags</Text>
                      <TextInput
                        style={[styles.detailModalInputSingle, { backgroundColor: "#f5f5f5", color: "#666" }]}
                        value={tags}
                        editable={false}
                      />
                    </View>
                  </View>
                </>
              )}

              <View style={styles.detailModalFormGrid}>
                <View style={[styles.detailModalFormField, { zIndex: 20 }]}> 
                  <Text style={styles.detailModalLabel}>Priority *</Text>
                  <AdoDropdown
                    value={priority}
                    options={PRIORITY_OPTIONS}
                    onChange={(v) => { setPriority(v); handleBlur("priority", v); }}
                    openKey="priority"
                    currentOpenKey={openDropdown}
                    setOpenKey={setOpenDropdown}
                  />
                  {!!errors.priority && touched.priority && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginTop: 2 }}>{errors.priority}</Text>
                  )}
                </View>
                <View style={[styles.detailModalFormField, { zIndex: 10 }]}> 
                  <Text style={styles.detailModalLabel}>Severity *</Text>
                  <AdoDropdown
                    value={severity}
                    options={severityOptions}
                    onChange={(v) => { setSeverity(v); handleBlur("severity", v); }}
                    openKey="severity"
                    currentOpenKey={openDropdown}
                    setOpenKey={setOpenDropdown}
                  />
                  {!!errors.severity && touched.severity && (
                    <Text style={{ color: "#C12C01", fontSize: 11, marginTop: 2 }}>{errors.severity}</Text>
                  )}
                </View>
              </View>
            </ScrollView>


            <View style={styles.detailModalFooter}>
              <Pressable
                style={styles.btnGhost}
                onPress={() => {
                  if (isDirty()) setShowDiscardConfirm(true);
                  else onCloseAdo();
                }}
              >
                <Text style={styles.btnGhostText}>Cancel</Text>
              </Pressable>
              <Pressable
                style={[styles.btnPrimary, (isAdoSubmitting || !isFormValid()) && { opacity: 0.65 }]}
                onPress={handleAdoSubmit}
                disabled={isAdoSubmitting || !isFormValid()}
              >
                {isAdoSubmitting ? (
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                    <ActivityIndicator size="small" color="#ffffff" />
                    <Text style={styles.btnPrimaryText}>Creating...</Text>
                  </View>
                ) : (
                  <Text style={styles.btnPrimaryText}>Submit to ADO</Text>
                )}
              </Pressable>
            </View>

            {/* Inline discard confirmation */}
            <Modal visible={showDiscardConfirm} transparent animationType="fade" onRequestClose={() => setShowDiscardConfirm(false)}>
              <View style={discardStyles.overlay}>
                <View style={discardStyles.card}>
                  <View style={discardStyles.header}>
                    <Text style={discardStyles.title}>Discard ADO work item?</Text>
                    <Pressable onPress={() => setShowDiscardConfirm(false)} style={discardStyles.closeBtn}>
                      <MaterialIcons name="close" size={22} color="#363636" />
                    </Pressable>
                  </View>
                  <View style={discardStyles.body}>
                    <Text style={discardStyles.message}>All entered information will be lost.</Text>
                  </View>
                  <View style={discardStyles.footer}>
                    <TouchableOpacity
                      style={[discardStyles.btn, discardStyles.btnSecondary]}
                      onPress={() => setShowDiscardConfirm(false)}
                    >
                      <Text style={discardStyles.btnSecondaryText}>Stay</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[discardStyles.btn, discardStyles.btnPrimary]}
                      onPress={() => {
                        setShowDiscardConfirm(false);
                        setAdoProject(initialState.current.adoProject);
                        setWorkItemType(initialState.current.workItemType);
                        setPriority(initialState.current.priority);
                        setSeverity(initialState.current.severity);
                        setTitle(initialState.current.title);
                        setDescription(initialState.current.description);
                        setAcceptanceCriteria(initialState.current.acceptanceCriteria);
                        setTargetDate(initialState.current.targetDate);
                        setAdoBoard(initialState.current.adoBoard);
                        setAssignedToAdo(initialState.current.assignedToAdo);
                        setTags(initialState.current.tags);
                        setTouched({});
                        setErrors({});
                        onCloseAdo();
                      }}
                    >
                      <Text style={discardStyles.btnPrimaryText}>Discard</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </Modal>
          </View>
        </View>
      </Modal>
    </>
  );
  };

export default TicketDetailActionModals;

// ── Merge modal local styles ───────────────────────────────────────────────
const mergeStyles = StyleSheet.create({
  // Selected list header
  selectedHeader: {
    marginTop: 2,
  },
  selectedCount: {
    fontWeight: "400",
    color: "#6b7280",
  },
  // Empty-state placeholder for selected list
  emptySelected: {
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 8,
    borderStyle: "dashed",
    paddingVertical: 14,
    paddingHorizontal: 12,
    marginBottom: 4,
    alignItems: "center",
  },
  emptySelectedText: {
    fontSize: 12,
    color: "#9099aa",
    fontStyle: "italic",
  },
  // Selected ticket card
  selectedCard: {
    flexDirection: "row",
    alignItems: "flex-start",
    borderWidth: 1,
    borderColor: "#005CBC",
    backgroundColor: "#E8F0FB",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 8,
    gap: 8,
  },
  selectedCardInfo: {
    flex: 1,
  },
  selectedCardTitle: {
    fontSize: 13,
    fontWeight: "500",
    color: "#2D2D2D",
    lineHeight: 18,
    marginTop: 2,
  },
  selectedCardMeta: {
    marginTop: 4,
    gap: 2,
  },
  selectedMetaItem: {
    fontSize: 11,
    color: "#555",
    lineHeight: 16,
  },
  selectedMetaLabel: {
    fontWeight: "600",
    color: "#3A3A3A",
  },
  removeBtn: {
    padding: 4,
    borderRadius: 4,
    backgroundColor: "#FDECEA",
    alignSelf: "flex-start",
  },
  // Search
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    backgroundColor: "#ffffff",
    paddingHorizontal: 10,
    marginBottom: 8,
    gap: 6,
  },
  searchIcon: {
    marginRight: 2,
  },
  searchInput: {
    flex: 1,
    height: 38,
    fontSize: 13,
    color: "#2D2D2D",
    paddingVertical: 0,
  },
  searchClear: {
    padding: 4,
  },
  searchHint: {
    fontSize: 12,
    color: "#9099aa",
    marginBottom: 8,
    fontStyle: "italic",
  },
  // Result card
  resultCard: {
    flexDirection: "row",
    alignItems: "flex-start",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 8,
    gap: 10,
  },
  resultCardDisabled: {
    backgroundColor: "#F9F9F9",
    borderColor: "#E0E0E0",
  },
  resultCardLeft: {
    flex: 1,
  },
  resultTopRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 3,
  },
  statusChip: {
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 12,
  },
  statusChipText: {
    fontSize: 11,
    fontWeight: "600",
  },
  resultMetaGrid: {
    marginTop: 5,
    gap: 2,
  },
  resultMetaItem: {
    fontSize: 11,
    color: "#555",
    lineHeight: 16,
  },
  resultMetaLabel: {
    fontWeight: "600",
    color: "#3A3A3A",
  },
  disabledText: {
    color: "#B0B0B0",
  },
  ineligibleNote: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginTop: 5,
  },
  ineligibleNoteText: {
    fontSize: 11,
    color: "#818181",
    fontStyle: "italic",
  },
  // Add to Merge button
  addBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    borderWidth: 1.5,
    borderColor: "#034895",
    borderRadius: 20,
    paddingVertical: 6,
    paddingHorizontal: 12,
    alignSelf: "flex-start",
    marginTop: 2,
  },
  addBtnDisabled: {
    borderColor: "#D0D0D0",
    backgroundColor: "#F4F4F4",
  },
  addBtnText: {
    fontSize: 12,
    fontWeight: "700",
    color: "#034895",
  },
  addBtnTextDisabled: {
    color: "#B0B0B0",
  },
  btnDisabled: {
    opacity: 0.45,
  },
  /** Inline error banner shown when selected tickets are no longer eligible */
  validationErrorBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    backgroundColor: "#FFF0EE",
    borderWidth: 1,
    borderColor: "#F5C2BB",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginTop: 12,
  },
  validationErrorText: {
    flex: 1,
    fontSize: 13,
    color: "#C12C01",
    fontWeight: "500",
  },
  // Impact summary card
  impactCard: {
    borderWidth: 1,
    borderColor: "#C6D9F5",
    backgroundColor: "#F3F7FD",
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginTop: 12,
    marginBottom: 10,
    gap: 10,
  },
  impactHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 4,
  },
  impactHeaderText: {
    fontSize: 13,
    fontWeight: "700",
    color: "#034895",
  },
  impactRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
  },
  impactRowText: {
    flex: 1,
    fontSize: 12,
    color: "#3A3A3A",
    lineHeight: 18,
  },
  impactRowLabel: {
    fontWeight: "700",
    color: "#2D2D2D",
  },
  impactUrgency: {
    fontWeight: "700",
  },
});

// ── Final merge confirmation dialog styles ─────────────────────────────────
const confirmStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.45)",
    paddingHorizontal: 24,
  },
  card: {
    width: "100%",
    maxWidth: 440,
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: 24,
    shadowColor: "#000",
    shadowOpacity: 0.2,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 6 },
    elevation: 10,
  },
  iconRow: {
    alignItems: "center",
    marginBottom: 14,
  },
  iconWrap: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: "#E8F0FB",
    justifyContent: "center",
    alignItems: "center",
  },
  title: {
    fontSize: 16,
    fontWeight: "700",
    color: "#111827",
    textAlign: "center",
    marginBottom: 10,
    lineHeight: 22,
  },
  body: {
    fontSize: 13,
    color: "#4B4B4B",
    textAlign: "center",
    lineHeight: 20,
    marginBottom: 20,
  },
  footer: {
    flexDirection: "row",
    gap: 10,
    justifyContent: "center",
  },
  btnCancel: {
    flex: 1,
    borderWidth: 1.5,
    borderColor: "#D0D0D0",
    borderRadius: 24,
    paddingVertical: 12,
    alignItems: "center",
  },
  btnCancelText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#374151",
  },
  btnMerge: {
    flex: 1,
    flexDirection: "row",
    gap: 6,
    backgroundColor: "#034895",
    borderRadius: 24,
    paddingVertical: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  btnMergeText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#ffffff",
  },
  // ServiceNow conflict info box inside confirm dialog
  snConflictBox: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    backgroundColor: "#E8F6FB",
    borderWidth: 1,
    borderColor: "#93C5FD",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 16,
  },
  snConflictText: {
    flex: 1,
    fontSize: 12,
    color: "#1e3a5f",
    lineHeight: 18,
  },
});

// ── Discard confirmation styles (private to this file) ─────────────────────
const discardStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.5)",
    padding: 20,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    width: "90%",
    maxWidth: 400,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
  },
  title: {
    fontSize: 18,
    fontWeight: "600",
    color: "#212121",
    flex: 1,
  },
  closeBtn: {
    padding: 4,
  },
  body: {
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
  message: {
    fontSize: 15,
    lineHeight: 22,
    color: "#424242",
    textAlign: "center",
  },
  footer: {
    padding: 16,
    flexDirection: "row",
    justifyContent: "center",
    gap: 12,
  },
  btn: {
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    minWidth: 100,
  },
  btnSecondary: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#0066CC",
  },
  btnPrimary: {
    backgroundColor: "#0066CC",
  },
  btnSecondaryText: {
    color: "#0066CC",
    fontSize: 15,
    fontWeight: "600",
  },
  btnPrimaryText: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "600",
  },
});
